<template>
  <div v-if="existingTimer" id="timer" class="col-md-2 col-sm-3 col-xs-5 container" style="display: block">
    <div id="timer-errors" class="error-list"></div>
    <div class="top-message">
      <div class="last-action">{{ lastAction }}</div>
      <div v-if="isPaused" class="first-started-time">{{ firstStartedTimeText }}</div>
      <div class="close-btn">
        <GekkoButton
          icon="fa-solid fa-xmark"
          variant="link"
          class="close"
          v-gk-confirm="{
            title: $gettext('Close timer?'),
            message: $gettext('Your hours will not be saved if you close the timer. Are you sure?'),
            options: {
              variant: 'danger',
              confirmText: $gettext('Yes'),
            },
            callback: destroyTimer,
          }"
        />
      </div>
    </div>
    <div class="row timer-clock">
      <div class="col-lg-12 col-md-12 col-sm-12 col-xs-12">
        <div id="timer-button" @click="toggleCounter()" title="Pause timer">
          <font-awesome-icon aria-hidden="true" :icon="isPaused ? 'fa-solid fa-play' : 'fa-solid fa-pause'" />
        </div>
        <div class="countdown is-countdown">
          <StopWatch
            v-show="!isPaused"
            ref="stop_watch"
            @mounted="updateAmounts()"
            :running="!isPaused"
            :since="existingTimer.passed_seconds"
          />
        </div>
      </div>
    </div>
    <form id="timer-form" ref="timerForm" action="" class="">
      <input id="timer-id" name="id" type="hidden" v-model="existingTimer.id" autocomplete="off" />
      <input id="hours_hidden_date" type="hidden" name="date" autocomplete="off" v-model="currentDate" />

      <b-form-row v-if="isPaused" class="timer-amount">
        <b-col cols="6">
          <b-input-group prepend="h" class="mb-2 mr-sm-2 mb-sm-0">
            <b-form-input
              id="amount_hours"
              :formatter="formatAmountHours"
              type="number"
              pattern="\d*"
              min="0"
              max="24"
              step="1"
              name="amount_hours"
              v-model="amount.hours"
            />
          </b-input-group>
        </b-col>
        <b-col cols="6">
          <b-input-group prepend="m" class="mb-2 mr-sm-2 mb-sm-0">
            <b-form-input
              id="amount_minutes"
              :formatter="formatAmountMinutes"
              type="number"
              pattern="\d*"
              min="0"
              max="59"
              step="1"
              name="amount_minutes"
              v-model="amount.minutes"
            />
          </b-input-group>
        </b-col>
      </b-form-row>

      <div class="form-group timer-projects">
        <HoursProjectSelector v-model="project" @change="projectSelected()" @deselect="projectDeselected" />
        <div class="form-required text-danger" v-if="formWarnings.project">
          <span>{{ $gettext('Select a project to save this entry') }}</span>
        </div>
      </div>

      <div v-if="isPaused" class="form-group timer-desc">
        <b-form-input
          id="amount_minutes"
          type="text"
          name="description"
          v-model="existingTimer.description"
          :placeholder="$gettext('what did you work on?')"
        />
      </div>

      <div v-if="isPaused" class="form-group timer-actions">
        <GekkoButton
          id="save_time"
          @click.prevent="saveHour()"
          :text="$gettext('Save')"
          class="position-relative"
          :loading="isWaitingForResponse"
        />
      </div>
    </form>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import GekkoButton from '@/components/GekkoButton'
import TimerService from '@/store/services/timer.service.js'
import HourService from '@/store/services/hour.service.js'
import HoursProjectSelector from '@/components/hours/HoursProjectSelector'
import StopWatch from '@/components/hours/StopWatch'
export default {
  name: 'HoursTimer',
  components: { HoursProjectSelector, StopWatch, GekkoButton },
  data() {
    return {
      existingTimer: null,
      project: null,
      layoutFull: false,
      amount: { hours: 0, minutes: 0 },
      currentDate: false,
      formWarnings: { project: false },
      saving: false,
    }
  },
  computed: {
    ...mapGetters({
      currentUser: 'main/getCurrentUser',
      getCompanyId: 'main/getCompanyId',
      isWaitingForResponse: 'main/isWaitingForResponse',
    }),
    isPaused() {
      return !!this.existingTimer.paused_timestamp
    },
    lastAction() {
      const { modified, total_paused_seconds } = this.existingTimer
      const time = modified.slice(0, 5)
      let label = this.$gettext('Started at ')
      if (this.isPaused) {
        label = this.$gettext('Last paused at ')
      } else if (total_paused_seconds != 0) {
        label = this.$gettext('Last started at ')
      }
      return label + time
    },
    firstStartedTimeText() {
      const { start_timestamp } = this.existingTimer
      if (!start_timestamp) return ''

      const time = new Date(start_timestamp).toString('HH:mm')
      return this.$gettext('Started at ') + time
    }
  },
  created() {
    TimerService.getTimers().then((response) => {
      if (response.data && response.data.length > 0) {
        this.existingTimer = response.data[0]
        if (this.existingTimer.project) {
          this.project = {}
          this.project.id = this.existingTimer.project
        }
      }
    })
  },
  methods: {
    ...mapMutations({
      setWaitingForResponse: 'main/SET_WAITING_FOR_RESPONSE',
    }),
    projectSelected() {
      this.formWarnings.project = false
      TimerService.changeTimerProject(this.existingTimer.id, { project: this.project.id })
    },
    projectDeselected() {
      this.formWarnings.project = false
      TimerService.changeTimerProject(this.existingTimer.id, { project: null })
    },
    toggleCounter() {
      if (this.isPaused) {
        this.resumeCounter()
      } else {
        this.pauseCounter()
      }
    },
    updateAmounts() {
      const seconds = this.$refs.stop_watch.getTime()
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      this.amount.hours = hours < 10 ? '0' + hours : hours
      this.amount.minutes = minutes < 10 ? '0' + minutes : minutes
      this.currentDate = Date.today().toString('yyyy-MM-dd')
    },
    resumeCounter() {
      TimerService.continueTimer(this.existingTimer.id).then((response) => {
        this.existingTimer = response.data
      })
    },
    pauseCounter() {
      TimerService.pauseTimer(this.existingTimer.id).then((response) => {
        if (window.mixpanel) {
          window.mixpanel.track('Pause timer', {})
        }
        this.existingTimer = response.data
        this.updateAmounts()
      })
    },
    saveHour() {
      if (this.formValidation() && !this.saving) {
        this.saving = true
        this.setWaitingForResponse(true)
        const formData = new FormData(this.$refs.timerForm)
        formData.set('date', this.existingTimer.start_timestamp)

        if (this.project && this.project.id) {
          formData.append('project', this.project.id)
        }
        formData.append('company_profile', this.getCompanyId)

        const amount = parseFloat(this.amount.hours) + parseFloat(this.amount.minutes) / 60
        formData.append('amount', parseFloat(amount).toFixed(2))
        formData.append('created_by', this.currentUser.id)
        HourService.create(formData)
          .then(() => {
            if (window.mixpanel) {
              window.mixpanel.track('Hours: create', {
                amount: amount,
                source: 'timer',
                'has description': this.existingTimer.description !== '',
              })
            }
            this.$store.commit('setAlert', {
              title: this.$gettext('Hour Created'),
              text: this.$gettext('Your time entry has been saved'),
              variant: 'success',
              duration: 4,
            })
            this.$store.commit('filterStore/SET_UPDATE_FILTER_HOURS')
            this.$store.commit('hourStore/SET_REFRESH_TOTALS', { refresh: null, exclude: null })
            this.destroyTimer()
          })
          .finally(() => {
            this.setWaitingForResponse(false)
          })
      } else {
        this.showFormAlerts()
      }
    },
    formatAmountHours(value) {
      return this.checkNumberInRange(0, 23, value)
    },
    formatAmountMinutes(value) {
      return this.checkNumberInRange(0, 59, value)
    },
    checkNumberInRange(min, max, num) {
      let retVal
      if (isNaN(num)) {
        retVal = '00'
      } else {
        num = Math.floor(num)
        retVal = num >= min && num <= max ? num : '00'
        retVal = retVal < 10 ? '0' + retVal : retVal
      }
      // only 2 characters, getting only 2 on the right
      return retVal.toString().substr(retVal.length - 2)
    },
    formValidation() {
      let isValid = true
      return isValid
    },
    showFormAlerts() {
      if (this.formWarnings.project) {
        this.$store.commit('setAlert', {
          title: this.$gettext('Error creating hour'),
          text: this.$gettext('No project selected'),
          variant: 'danger',
          duration: 4,
        })
      }
    },
    newTimer() {
      if (!this.existingTimer) {
        this.saving = false
        const formData = new FormData()
        formData.append('company_profile', this.getCompanyId)
        TimerService.startTimer(formData).then((response) => {
          if (window.mixpanel) {
            window.mixpanel.track('launch timer', {})
          }
          this.existingTimer = response.data
        })
      }
    },
    destroyTimer() {
      TimerService.deleteTimer(this.existingTimer.id).then(() => {
        this.existingTimer = null
      })
    },
  },
}
</script>
<style lang="scss">
.timer-projects {
  .dropdown-menu {
    width: auto;
    transform: translateY(-100%) !important;
  }
}
</style>
<style lang="scss" scoped>
#timer {
  position: fixed;
  bottom: 0;
  left: 20px;
  background: #fff;
  border-top: 1px solid #cfcfd6;
  border-right: 1px solid #cfcfd6;
  border-left: 1px solid #cfcfd6;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  padding: 15px 15px 5px;
  z-index: 999;
  box-shadow: 0px 0px 4px #ccc;
  p {
    margin: 0px;
  }
  #timer-button {
    margin: 10px;
    .svg-inline--fa {
      font-size: 30px;
      margin: 8px 0 0;
      cursor: pointer;
    }
  }
  .timer-clock {
    text-align: center;
  }
  .timer-projects {
    margin-top: 10px;
  }
  .timer-amount {
    margin-top: 5px;
  }
  .minutes-wrap {
    margin-left: 20px;
  }
  .top-message {
    font-size: 14px;
    line-height: 16px;
    color: #999;
    position: relative;
    padding-bottom: 10px;
    .last-action,
    .first-started-time {
      display: block;
    }
    .close-btn {
      position: absolute;
      top: 0;
      right: 0;
      .close {
        font-size: 14px;
        line-height: 1;
        .svg-inline--fa {
          font-size: 16px;
        }
        &:hover {
          opacity: 0.5;
        }
      }
    }
  }
  #timer-form {
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      /* display: none; <- Crashes Chrome on hover */
      -webkit-appearance: none;
      margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
    }
    input[type='number'] {
      -moz-appearance: textfield; /* Firefox */
    }
    .form-group {
      margin-bottom: 10px;
    }
    .form-control {
      box-shadow: inset 0 1px 1px rgb(0 0 0 / 8%);
      background-color: $inputBackgroundColor;
      &::placeholder {
        color: #cdcdcd;
      }
      &:focus {
        box-shadow: none;
      }
    }
    #amount_minutes,
    #amount_hours {
      padding-left: 5px;
      padding-right: 5px;
      text-align: center;
    }
    #save_time {
      margin-top: 10px;
    }
    .form-required {
      font-size: 14px;
      line-height: 1;
      margin-top: 5px;
    }
  }
}
</style>
